/**
 * API service for backend communication
 */

import { ENV } from '../config/env'
import { authenticatedFetch } from './auth-interceptor'

const API_BASE_URL = ENV.API_BASE_URL

/**
 * Standardized API response envelope format
 * All success responses from the backend use this format:
 * { data: T, meta?: Record<string, unknown> }
 */
export type ApiResponseEnvelope<T> = { data: T; meta?: Record<string, unknown> } | T // Fallback for legacy responses without envelope

interface ApiErrorResponse {
    message?: string
    error?: string
    code?: string
    translation_key?: string
    details?: Record<string, unknown>
    request_id?: string
}

class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public error?: string,
        public translationKey?: string,
        public details?: Record<string, unknown>,
        public requestId?: string
    ) {
        super(message)
        this.name = 'ApiError'
        // For backward compatibility, also set code to error
        if (error) {
            ;(this as ApiError & { code?: string }).code = error
        }
    }
}

class ApiService {
    private baseURL: string

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL
    }

    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const url = `${this.baseURL}${endpoint}`

        try {
            const response = await authenticatedFetch(url, options)

            if (!response.ok) {
                const errorData = (await response.json().catch(() => ({}))) as ApiErrorResponse
                throw new ApiError(
                    errorData.message || 'Request failed',
                    response.status,
                    errorData.error || errorData.code, // Support both 'error' and legacy 'code'
                    errorData.translation_key,
                    errorData.details,
                    errorData.request_id
                )
            }

            const responseData = (await response.json()) as ApiResponseEnvelope<T>

            // Unwrap response envelope: {data, meta} -> data
            if (responseData && typeof responseData === 'object' && 'data' in responseData) {
                return responseData.data
            }

            // Fallback for responses that don't use envelope (shouldn't happen with new backend)
            return responseData
        } catch (error) {
            if (error instanceof ApiError) {
                throw error
            }
            if (error instanceof Error && error.message.includes('Authentication failed')) {
                // Re-throw auth errors to be handled by the calling code
                throw error
            }
            throw new ApiError('Network error', 0)
        }
    }

    // Health check
    async healthCheck(): Promise<unknown> {
        return this.request<unknown>('/health')
    }

    // Authentication
    async login(email: string, password: string) {
        return this.request('/api/v1/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        })
    }

    async register(
        username: string,
        email: string,
        password: string,
        userLang: string,
        targetLang: string
    ) {
        return this.request('/api/v1/auth/register', {
            method: 'POST',
            body: JSON.stringify({
                username,
                email,
                password,
                user_lang: userLang,
                target_lang: targetLang,
            }),
        })
    }

    // Translation
    async submitTranslation(
        sentenceId: string,
        userTranslation: string,
        userLang: string,
        targetLang: string
    ) {
        return this.request('/api/v1/translate', {
            method: 'POST',
            body: JSON.stringify({
                sentence_id: sentenceId,
                user_translation: userTranslation,
                user_lang: userLang,
                target_lang: targetLang,
            }),
        })
    }

    // Translation Feedback
    async createFeedback(
        attemptId: string,
        feedbackValue: 'up' | 'down',
        lemmas?: Array<{ lemma: string; translations: string[] }>
    ) {
        return this.request<{
            id: string
            user_id: string
            attempt_id: string | null
            sentence_id: string
            sentence_text: string
            user_translation: string
            model_feedback: Record<string, unknown>
            judgment: 'correct' | 'close' | 'incorrect'
            lemmas: unknown
            feedback_value: 'up' | 'down'
            backend_version: string
            sub_prompt_id: string | null
            created_at: string
            updated_at: string
        }>('/api/v1/feedback', {
            method: 'POST',
            body: JSON.stringify({
                attempt_id: attemptId,
                feedback_value: feedbackValue,
                lemmas: lemmas || undefined,
            }),
        })
    }

    async updateFeedback(feedbackId: string, feedbackValue: 'up' | 'down') {
        return this.request<{
            id: string
            user_id: string
            attempt_id: string | null
            sentence_id: string
            sentence_text: string
            user_translation: string
            model_feedback: Record<string, unknown>
            judgment: 'correct' | 'close' | 'incorrect'
            lemmas: unknown
            feedback_value: 'up' | 'down'
            backend_version: string
            sub_prompt_id: string | null
            created_at: string
            updated_at: string
        }>(`/api/v1/feedback/${feedbackId}`, {
            method: 'PATCH',
            body: JSON.stringify({
                feedback_value: feedbackValue,
            }),
        })
    }

    async deleteFeedback(feedbackId: string) {
        return this.request<{ message: string }>(`/api/v1/feedback/${feedbackId}`, {
            method: 'DELETE',
        })
    }

    // Usage quota
    async getCurrentUsage(): Promise<{
        monthly_usage_percent: number
        reset_at: string
        is_unlimited: boolean
    }> {
        return this.request('/api/v1/auth/me/usage')
    }

    // Admin usage management
    async setUserUsageLimit(
        userId: string,
        limit: number | null
    ): Promise<{ user_id: string; monthly_usage_limit_override: number | null }> {
        return this.request(`/api/v1/admin/users/${userId}/usage-limit`, {
            method: 'POST',
            body: JSON.stringify({ limit }),
        })
    }

    async resetUserUsage(userId: string): Promise<{ user_id: string; cost_units_used: number }> {
        return this.request(`/api/v1/admin/users/${userId}/usage-reset`, {
            method: 'POST',
        })
    }

    async evaluateTranslation(
        corpusId: string,
        storyId: string,
        sentenceId: string,
        original: string,
        userTranslation: string,
        context?: string,
        lemmaGlossary?: Array<{ lemma: string; translations: string[] }>,
        signal?: AbortSignal
    ) {
        const body: {
            corpus_id: string
            story_id: string
            sentence_id: string
            original: string
            user_translation: string
            context?: string
            lemma_glossary?: Array<{ lemma: string; translations: string[] }>
        } = {
            corpus_id: corpusId,
            story_id: storyId,
            sentence_id: sentenceId,
            original,
            user_translation: userTranslation,
        }

        if (context) {
            body.context = context
        }

        if (lemmaGlossary && lemmaGlossary.length > 0) {
            body.lemma_glossary = lemmaGlossary
        }

        return this.request('/api/v1/translate/evaluate', {
            method: 'POST',
            body: JSON.stringify(body),
            signal,
        })
    }
}

/**
 * Factory function to create a new ApiService instance.
 * Use this in tests for fresh, isolated instances.
 */
export const createApiService = (baseURL?: string): ApiService => new ApiService(baseURL)

/**
 * Default singleton instance for production use.
 */
export const apiService = createApiService()
export { ApiError, ApiService }
